import { useState } from 'react';
import { AlertCircle, CheckCircle2, Lock, PlugZap, ShieldOff } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { apiPost } from '../api/client';

/**
 * The connection details for a customer's own MySQL server (the "self-hosted" database
 * option), plus the optional "Test connection" action.
 *
 * ONE component, used by all three create surfaces — admin/reseller create, the signup
 * wizard, and an owner adding another business. They previously could not drift because only
 * one of them had these fields at all; now that all three do, a single component is what
 * keeps "what the form accepts" identical everywhere. The server-side validator
 * (validateSelfHostedCreds) is the matching single source on the other side.
 */

export interface ByoDbValue {
  host: string;
  /** Kept as a string because it comes from a text input; coerced at the call site. */
  port: string;
  dbName: string;
  username: string;
  password: string;
}

export const EMPTY_BYO_DB: ByoDbValue = { host: '', port: '3306', dbName: '', username: '', password: '' };

/**
 * Client-side shape check, shared by every wizard's "can I submit" logic.
 *
 * Deliberately mirrors the server's rules rather than being laxer: a form that accepts a
 * value the server then rejects makes the customer guess which field was wrong, and the
 * server's message arrives after they have already committed to the step.
 */
export function byoDbError(v: ByoDbValue): string | null {
  if (!v.host.trim()) return 'Enter the address of your MySQL server.';
  if (!v.dbName.trim()) return 'Enter the name of the database to use.';
  if (!v.username.trim()) return 'Enter the database username.';
  if (!v.password) return 'Enter the database password.';
  const port = Number(v.port);
  if (!Number.isInteger(port) || port <= 0 || port > 65535) return 'Port must be a number between 1 and 65535.';
  if (!/^[A-Za-z0-9_][A-Za-z0-9_-]{0,63}$/.test(v.dbName.trim())) {
    return 'Database name must be letters, numbers, underscore or dash, and cannot start with a dash.';
  }
  if (!/^[A-Za-z0-9_][A-Za-z0-9_-]{0,63}$/.test(v.username.trim())) {
    return 'Database username must be letters, numbers, underscore or dash, and cannot start with a dash.';
  }
  return null;
}

/** The payload shape POST /businesses and POST /auth/signup both expect. */
export function toByoDbCreds(v: ByoDbValue) {
  return {
    host: v.host.trim(),
    port: Number(v.port) || 3306,
    dbName: v.dbName.trim(),
    username: v.username.trim(),
    password: v.password,
  };
}

type TestResult = { ok: boolean; message: string; encrypted?: boolean };

export function ByoDbFields({
  value,
  onChange,
  /**
   * Show the "Test connection" button. OFF on public signup by design: the endpoint behind it
   * opens a connection to any host you name and tells you what happened, which is a network
   * prober if it sits in front of a login. Signup performs the same check inside its own
   * verified submit instead, so nothing is lost but the button.
   */
  canTest = false,
}: {
  value: ByoDbValue;
  onChange: (v: ByoDbValue) => void;
  canTest?: boolean;
}) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const set = (patch: Partial<ByoDbValue>) => {
    setResult(null); // any edit invalidates the previous verdict
    onChange({ ...value, ...patch });
  };

  async function test() {
    const invalid = byoDbError(value);
    if (invalid) {
      setResult({ ok: false, message: invalid });
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const r = await apiPost<TestResult>('/businesses/db-connection-test', toByoDbCreds(value));
      setResult(r);
    } catch (e) {
      setResult({ ok: false, message: e instanceof Error ? e.message : 'We could not reach that database.' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          label="Database host"
          hint="The public address of your MySQL server."
          value={value.host}
          onChange={(e) => set({ host: e.target.value })}
          required
        />
        <Input label="Port" value={value.port} onChange={(e) => set({ port: e.target.value })} inputMode="numeric" />
        <Input
          label="Database name"
          hint="A new, empty database — we never write over an existing one."
          value={value.dbName}
          onChange={(e) => set({ dbName: e.target.value })}
          required
        />
        <Input label="Username" value={value.username} onChange={(e) => set({ username: e.target.value })} required />
        <Input
          label="Password"
          type="password"
          value={value.password}
          onChange={(e) => set({ password: e.target.value })}
          required
          className="sm:col-span-2"
        />
      </div>

      {canTest && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={test}
            loading={testing}
            iconLeft={<PlugZap className="w-4 h-4" />}
            className="min-h-[44px]"
          >
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            Checks we can reach it and that it is empty.
          </span>
        </div>
      )}

      {result && (
        <div
          role="status"
          className={
            'flex items-start gap-2 rounded-md border p-3 text-xs ' +
            (result.ok
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200')
          }
        >
          {result.ok ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-px" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-px" />
          )}
          <span>{result.message}</span>
        </div>
      )}

      {/*
        The honest version of what used to be a flat "we connect over TLS" claim, which the
        code did not implement at all. We try an encrypted connection first and fall back to
        an unencrypted one if the server does not offer TLS — and record which happened, so
        the answer is a fact about each customer rather than a promise on a page.
      */}
      <p className="flex items-start gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
        {result?.ok && result.encrypted === false ? (
          <ShieldOff className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
        ) : (
          <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
        )}
        <span>
          We connect over an encrypted link when your server supports it, and fall back to an
          unencrypted one when it does not. Your password is encrypted at rest either way, and we
          set up the POS tables in the database you name here.
        </span>
      </p>
    </div>
  );
}
